import React, { useMemo } from 'react';
import { RefreshCw, Search, X, Star } from 'lucide-react';
import { LOCAL_EXERCISES } from '../data/exercises';

export default function SwapModal({ swapModal, setSwapModal, swapSearch, setSwapSearch, exerciseDatabase, executeSwap }) {
    const oldName = swapModal?.oldName || '';

    const unifiedList = useMemo(() => {
        const seen = new Set();
        const list = [];

        const add = (rawName, tip = '', primary = '') => {
            const clean = String(rawName || '').trim();
            if (!clean) return;
            const key = clean.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            list.push({
                name: clean,
                tip: tip || 'Concentreer je op een veilige en gecontroleerde uitvoering.',
                primary: String(primary || '').toLowerCase()
            });
        };

        if (Array.isArray(exerciseDatabase)) {
            exerciseDatabase.forEach(e => {
                add(e.exercise_name || e.name || e.title, e.tip || e.description || e.desc, e.primary_muscles || e.muscleGroup || e.muscle_group);
            });
        }
        if (Array.isArray(LOCAL_EXERCISES)) {
            LOCAL_EXERCISES.forEach(e => {
                add(e.title || e.name, e.tip || e.desc, e.muscleGroup || '');
            });
        }

        // Bepaal spiergroep/patroon van de huidige oefening om relevante swaps bovenaan te zetten
        const currentLower = oldName.toLowerCase();
        const currentMatch = list.find(item => item.name.toLowerCase() === currentLower);
        const targetMuscle = currentMatch?.primary || '';

        const getRelevanceScore = (item) => {
            const n = item.name.toLowerCase();
            if (n === currentLower) return -1;
            let score = 0;
            if (targetMuscle && item.primary && item.primary === targetMuscle) score += 10;
            if (currentLower.includes('squat') && (n.includes('squat') || n.includes('leg press') || n.includes('lunge') || n.includes('hack'))) score += 8;
            if (currentLower.includes('bench') && (n.includes('bench') || n.includes('chest press') || n.includes('push-up') || n.includes('fly'))) score += 8;
            if (currentLower.includes('deadlift') && (n.includes('deadlift') || n.includes('rdl') || n.includes('hip thrust') || n.includes('back extension'))) score += 8;
            if ((currentLower.includes('row') || currentLower.includes('pull')) && (n.includes('row') || n.includes('pull') || n.includes('lat') || n.includes('chin'))) score += 8;
            if (currentLower.includes('curl') && n.includes('curl')) score += 8;
            if ((currentLower.includes('tricep') || currentLower.includes('pushdown')) && (n.includes('tricep') || n.includes('pushdown') || n.includes('extension') || n.includes('dip'))) score += 8;
            if ((currentLower.includes('shoulder') || currentLower.includes('overhead') || currentLower.includes('raise')) && (n.includes('shoulder') || n.includes('overhead') || n.includes('raise') || n.includes('press'))) score += 6;
            return score;
        };

        return list
            .filter(item => item.name.toLowerCase() !== currentLower)
            .map(item => ({ ...item, relevance: getRelevanceScore(item) }))
            .sort((a, b) => {
                if (b.relevance !== a.relevance) return b.relevance - a.relevance;
                return a.name.localeCompare(b.name);
            });
    }, [exerciseDatabase, oldName]);

    if (!swapModal || !swapModal.isOpen) return null;

    const filtered = unifiedList.filter(e =>
        (e.name + ' ' + e.tip + ' ' + e.primary).toLowerCase().includes((swapSearch || '').toLowerCase())
    );

    return (
        <div className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-zinc-900 border border-zinc-700 p-6 rounded-3xl w-full max-w-lg shadow-2xl flex flex-col max-h-[85vh]">
                <div className="flex justify-between items-center mb-4 gap-2">
                    <div className="min-w-0">
                        <span className="text-[10px] font-black uppercase tracking-widest text-orange-500 block">Wissel Oefening</span>
                        <h3 className="text-lg sm:text-xl font-black text-white truncate flex items-center gap-2">
                            <RefreshCw size={18} className="text-orange-500 shrink-0"/> Vervang: {oldName}
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={() => { setSwapModal({ isOpen: false, idx: null, oldName: '' }); setSwapSearch(''); }}
                        className="text-gray-400 hover:text-white transition-colors p-2 bg-zinc-800 rounded-full border border-zinc-700 shrink-0"
                    >
                        <X size={20}/>
                    </button>
                </div>

                <div className="relative mb-3 shrink-0">
                    <Search className="absolute left-4 top-3.5 text-gray-400" size={20} />
                    <input
                        type="text"
                        placeholder="Zoek alternatief..."
                        value={swapSearch}
                        onChange={(e) => setSwapSearch(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-orange-500 outline-none transition-colors font-bold"
                        autoFocus
                    />
                </div>

                <div className="overflow-y-auto flex-1 space-y-2 pr-1 custom-scrollbar">
                    {filtered.map((ex, i) => (
                        <button
                            key={`${ex.name}-${i}`}
                            type="button"
                            onClick={() => executeSwap(ex.name)}
                            className="w-full text-left p-4 bg-zinc-800/50 hover:bg-orange-500/10 rounded-xl border border-zinc-700 hover:border-orange-500/50 transition-all"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-black text-blue-500 text-sm sm:text-base">{ex.name}</span>
                                {ex.relevance >= 8 && (
                                    <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-orange-500/15 text-orange-500 border border-orange-500/30 flex items-center gap-1 shrink-0">
                                        <Star size={10} fill="currentColor"/> Aanbevolen
                                    </span>
                                )}
                            </div>
                            {ex.tip && <div className="text-xs text-gray-400 mt-1 line-clamp-2">{ex.tip}</div>}
                        </button>
                    ))}

                    {swapSearch.trim() !== '' && !filtered.some(e => e.name.toLowerCase() === swapSearch.trim().toLowerCase()) && (
                        <button
                            type="button"
                            onClick={() => executeSwap(swapSearch.trim())}
                            className="w-full text-left p-4 bg-orange-500/15 hover:bg-orange-500/25 rounded-xl border border-orange-500/40 transition-all mt-2"
                        >
                            <div className="font-black text-orange-500">Vervang door "{swapSearch.trim()}"</div>
                            <div className="text-xs text-gray-400 mt-0.5">Gebruik je eigen ingevoerde oefeningnaam.</div>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
